import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoImage } from "./Logo";

const links = [
  { label: "About", href: "#about" },
  { label: "The Accelerator", href: "#program" },
  { label: "Curriculum", route: "/accelerator" },
  { label: "Who It Serves", href: "#who" },
  { label: "Pre-Launch", href: "#prelaunch" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      if (location.pathname !== link.route) navigate(link.route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" }), 600);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <button onClick={goHome} aria-label="SHE Global™ Institute home" data-testid="nav-home-link">
          <LogoImage className="h-12 lg:h-14 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-10" data-testid="nav-desktop-links">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-xs tracking-[0.25em] uppercase text-white hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go({ href: "#interest" })}
            data-testid="nav-express-interest-btn"
            className="text-xs tracking-[0.25em] uppercase border border-[#D4AF37] text-[#D4AF37] px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
          >
            Express Interest
          </button>
        </nav>

        <button
          className="lg:hidden text-[#D4AF37]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-black/95 border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <button
                  key={l.label}
                  onClick={() => go(l)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left text-sm tracking-[0.25em] uppercase text-white hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go({ href: "#interest" })}
                data-testid="nav-mobile-express-interest-btn"
                className="text-sm tracking-[0.25em] uppercase border border-[#D4AF37] text-[#D4AF37] px-6 py-4 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
              >
                Express Interest
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
